import { buildKeypath } from '@/data/idGenerator';
import type {
  DataContextType,
  ReportType,
  TemplateViewBlockTableType,
  ViewBlock,
} from '@/data/schemas';
import {
  applyPipes,
  evaluateTemplateExpression,
  getValueFromItem,
  parsePipes,
} from '@/data/templateExpressions';

interface BuildTableViewBlockOptions {
  block: TemplateViewBlockTableType;
  report: ReportType;
  sectionId: string;
  indices?: number[];
  context?: DataContextType | null;
}

/**
 * Build a table ViewBlock from template configuration and report data.
 * Fallbacks to live context when stored snapshot is unavailable.
 */
export function buildTableViewBlock({
  block,
  report,
  sectionId,
  indices,
  context,
}: BuildTableViewBlockOptions): Extract<ViewBlock, { type: 'table' }> {
  const keypath = buildKeypath(sectionId, block.id, indices);
  const stored = report.data.values[keypath];
  let sourceRows: unknown[] | null = Array.isArray(stored) ? stored : null;

  const prefersLive =
    context &&
    typeof block.rowSource === 'string' &&
    block.rowSource.includes('visit.scorecard.byDate');

  if ((prefersLive || !sourceRows) && context) {
    const resolved = evaluateTemplateExpression(
      block.rowSource,
      context as Record<string, unknown>
    );
    if (Array.isArray(resolved)) {
      sourceRows = resolved;
    }
  }

  const rows =
    sourceRows?.filter((row): row is Record<string, unknown> => {
      return row !== null && typeof row === 'object';
    }) ?? [];

  const columnDefs = block.columns.map((column) => ({
    id: column.id,
    label: column.label,
    fieldPath: column.fieldPath,
    width: column.width,
    align: column.align ?? 'left',
  }));

  const computedRows = rows.map((row, index) => ({
    id: `${block.id}_row_${index}`,
    values: Object.fromEntries(
      columnDefs.map((column) => [column.id, evaluateFieldPath(row, column.fieldPath)])
    ),
  }));

  return {
    type: 'table',
    id: block.id,
    columns: columnDefs,
    rows: computedRows,
    rowSource: block.rowSource,
    emptyMessage: block.emptyMessage ?? 'Nessun dato disponibile',
  };
}

function evaluateFieldPath(row: Record<string, unknown>, fieldPath: string): unknown {
  const segments = splitConcatenation(fieldPath);

  if (segments.length === 1) {
    return evaluateSegment(row, segments[0]);
  }

  return segments
    .map((segment) => {
      const value = evaluateSegment(row, segment);
      if (value === undefined || value === null) {
        return '';
      }
      return String(value);
    })
    .join('');
}

function evaluateSegment(row: Record<string, unknown>, segment: string) {
  const trimmed = segment.trim();
  if (!trimmed.length) return '';

  if (
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  ) {
    return trimmed.slice(1, -1);
  }

  const pipeIndex = trimmed.indexOf('|');
  const path = pipeIndex === -1 ? trimmed : trimmed.slice(0, pipeIndex).trim();
  const pipesExpression = pipeIndex === -1 ? undefined : trimmed.slice(pipeIndex + 1).trim();

  let value = getValueFromItem(row, path);

  if (pipesExpression) {
    value = applyPipes(value, parsePipes(pipesExpression));
  }

  return value;
}

function splitConcatenation(expression: string): string[] {
  const segments: string[] = [];
  let current = '';
  let inSingleQuote = false;
  let inDoubleQuote = false;

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];

    if (char === "'" && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote;
      current += char;
      continue;
    }

    if (char === '"' && !inSingleQuote) {
      inDoubleQuote = !inDoubleQuote;
      current += char;
      continue;
    }

    if (char === '+' && !inSingleQuote && !inDoubleQuote) {
      segments.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  if (current.length > 0) {
    segments.push(current);
  }

  return segments.length > 0 ? segments : [expression];
}
